import { MultiSelectChip } from '../../components/organisms/filter-container/filter-container.component';
import { BusinessStatus } from '../constants/business-status';
import { FilterType } from '../constants/filter-type';
import { ChipContentOption } from './chip-content-option';
import { CouponStatus } from '../constants/coupon-status';

/**
 * Deals with chip content
 */
export class ChipContent {
  /**
   * @constructor
   */
  constructor(
    public icon?: string,
    public value?: string,
    public filterType?: FilterType,
    public isActive?: boolean,
    public iconAfter?: string,
    public options?: ChipContentOption[]
  ) {}

  /**
   * Factory method for sort by instances
   * @param {ChipContentOption[]} options
   * @return {ChipContent}
   */
  static SortBy(options: ChipContentOption[]): ChipContent {
    return new ChipContent(
      undefined,
      options[0].text,
      FilterType.REWARDS_SORT_BY,
      false,
      'expand_more',
      options
    );
  }

  /**
   * Factory method for vip instances
   * @return {ChipContent}
   */
  static Vip(): ChipContent {
    return new ChipContent(
      undefined,
      'vip',
      FilterType.BUSINESS_VIP,
      false,
      undefined
    );
  }

  /**
   * Factory method for coupons instances
   * @return {ChipContent}
   */
  static Coupons(): ChipContent {
    return new ChipContent(
      undefined,
      'coupons',
      FilterType.ALL_COUPONS,
      false,
      undefined
    );
  }

  /**
   * Factory method for expired instances
   * @return {ChipContent}
   */
  static Expired(): ChipContent {
    return new ChipContent(
      undefined,
      'expired',
      FilterType.EXPIRED,
      false,
      undefined
    );
  }

  /**
   * Factory method for series F instances
   * @return {ChipContent}
   */
  static SeriesF(): ChipContent {
    return new ChipContent(
      undefined,
      'seriesF',
      FilterType.BUSINESS_SERIES_F,
      false
    );
  }

  /**
   * Factory method for series S instances
   * @return {ChipContent}
   */
  static SeriesS(): ChipContent {
    return new ChipContent(
      undefined,
      'seriesS',
      FilterType.BUSINESS_SERIES_S,
      false
    );
  }

  /**
   * Factory method for series S instances
   * @return {ChipContent}
   */
  static SeriesG(): ChipContent {
    return new ChipContent(
      undefined,
      'seriesG',
      FilterType.BUSINESS_SERIES_G,
      false
    );
  }

  /**
   * Factory method for printers instances
   * @return {ChipContent}
   */
  static Products(): ChipContent {
    return new ChipContent(
      undefined,
      'products',
      FilterType.REWARDS_PRODUCTS,
      false
    );
  }

  /**
   * Factory method for customer service instances
   * @return {ChipContent}
   */
  static CService(): ChipContent {
    return new ChipContent(
      undefined,
      'cservice',
      FilterType.REWARDS_CUSTOMER_SERVICE,
      false
    );
  }

  /**
   * Factory method for status multiselect instances
   * @return {MultiSelectChip[]}
   */
  static Status(): MultiSelectChip[] {
    const statusChips: MultiSelectChip[] = [
      {
        value: 'Active',
        isActive: false,
        filterType: 'Active',
        id: BusinessStatus.ACTIVE,
      },
      {
        value: 'Disabled',
        isActive: false,
        filterType: 'Disabled',
        id: BusinessStatus.DISABLED,
      },
      {
        value: 'Idle',
        isActive: false,
        filterType: 'Idle',
        id: BusinessStatus.IDLE,
      },
      {
        value: 'Invited',
        isActive: false,
        filterType: 'Invited',
        id: BusinessStatus.INVITED,
      },
    ];
    return statusChips;
  }

  /**
   * Factory method for status multiselect instances
   * @return {MultiSelectChip[]}
   */
  static StatusCoupon(): MultiSelectChip[] {
    const statusChips: MultiSelectChip[] = [
      {
        value: 'Active',
        isActive: false,
        filterType: 'Active',
        id: CouponStatus.ACTIVE,
      },
      {
        value: 'Redeemed',
        isActive: false,
        filterType: 'Redeemed',
        id: CouponStatus.REDEEMED,
      },
      {
        value: 'Expired',
        isActive: false,
        filterType: 'Expired',
        id: CouponStatus.EXPIRED,
      },
      {
        value: 'Canceled',
        isActive: false,
        filterType: 'Canceled',
        id: CouponStatus.CANCELED,
      },
    ];
    return statusChips;
  }

  /**
   * Factory method for points date range instances
   * @return {ChipContent}
   */
  static PointsDateRange(): ChipContent {
    return new ChipContent(
      undefined,
      'dateRange',
      FilterType.DATE_RANGE,
      false
    );
  }

  /**
   * Factory method for PointsAdded instances
   * @return {ChipContent}
   */
  static PointsAdded(): ChipContent {
    return new ChipContent('stars', 'added', FilterType.POINTS_ADDED, false);
  }

  /**
   * Factory method for points redeemed instances
   * @return {ChipContent}
   */
  static PointsRedeemed(): ChipContent {
    return new ChipContent(
      'confirmation_number',
      'redeemed',
      FilterType.POINTS_REDEEMED,
      false
    );
  }

  /**
   * Factory method for points expired instances
   * @return {ChipContent}
   */
  static PointsExpired(): ChipContent {
    return new ChipContent(
      'event_busy',
      'expired',
      FilterType.POINTS_EXPIRED,
      false
    );
  }

  /**
   * Factory method for package codes instances
   * @return {ChipContent}
   */
  static PackageCodesChip(): ChipContent {
    return new ChipContent(
      undefined,
      'packageCodes',
      FilterType.PACKAGE_CODES,
      true
    );
  }

  /**
   * Factory method for account data instances
   * @return {ChipContent}
   */
  static AccountDataChip(): ChipContent {
    return new ChipContent(
      undefined,
      'accountData',
      FilterType.ACCOUNT_DATA,
      false
    );
  }

  /**
   * Factory method for port report instances
   * @return {ChipContent}
   */
  static PortReportChip(): ChipContent {
    return new ChipContent(
      undefined,
      'portReport',
      FilterType.PORT_REPORT,
      false
    );
  }

  /**
   * Factory method for port batch processing instances
   * @return {ChipContent}
   */
  static PortBatchProcessingChip(): ChipContent {
    return new ChipContent(
      undefined,
      'portBatchProcessing',
      FilterType.PORT_BATCH_PROCESSING,
      false
    );
  }
}
